import React from 'react';
import {
  X,
  Clock,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Percent,
} from 'lucide-react';
import { AccountPosition } from '../types';

interface PositionDetailsModalProps {
  position: AccountPosition | null;
  isOpen: boolean;
  onClose: () => void;
}

const PositionDetailsModal: React.FC<PositionDetailsModalProps> = ({
  position,
  isOpen,
  onClose,
}) => {
  if (!isOpen || !position) return null;

  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number) => {
    if (isNaN(value)) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Get the last 10 activity log items from position.activityLog array
  const recentActivityLogs = position.activityLog
    ? position.activityLog.slice(-10).reverse() // Most recent first
    : [];

  // Get trade direction for icon
  const isBullish =
    position.tradeDirection === 'BULL' || position.tradeDirection === 'Up';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {isBullish ? (
              <TrendingUp className="w-6 h-6 text-green-500" />
            ) : (
              <TrendingDown className="w-6 h-6 text-red-500" />
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {position.symbol} Position Details
              </h2>
              <p className="text-sm text-gray-600">
                {position.tradeDirection} • {position.status}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="p-6 space-y-6">
            {/* Position Thesis */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <DollarSign className="w-5 h-5 mr-2" />
                Position Thesis
              </h3>
              {position.thesis ? (
                <div className="space-y-4">
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {position.thesis.originalReasoning}
                    </p>
                  </div>

                  {/* Thesis Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Expected Outcome
                      </h4>
                      <p className="text-sm text-gray-600">
                        {position.thesis.expectedOutcome}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Time Horizon
                      </h4>
                      <p className="text-sm text-gray-600">
                        {position.thesis.timeHorizon}
                      </p>
                    </div>
                    {position.thesis.targetPrice && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Target Price
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(position.thesis.targetPrice)}
                        </p>
                      </div>
                    )}
                    {position.thesis.stopLossPrice && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Stop Loss Price
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(position.thesis.stopLossPrice)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Key Assumptions */}
                  {position.thesis.keyAssumptions &&
                    position.thesis.keyAssumptions.length > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Key Assumptions
                        </h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {position.thesis.keyAssumptions.map(
                            (assumption, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-blue-500 mr-2">•</span>
                                {assumption}
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                  {/* Risk Factors */}
                  {position.thesis.riskFactors &&
                    position.thesis.riskFactors.length > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Risk Factors
                        </h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {position.thesis.riskFactors.map((risk, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-red-500 mr-2">•</span>
                              {risk}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  No thesis available for this position.
                </p>
              )}
            </div>

            {/* Position Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">
                      Allocated Capital
                    </p>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(position.allocatedCapital)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-blue-400" />
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">
                      Current Return
                    </p>
                    <p className="text-2xl font-bold text-green-900">
                      {position.actualReturn !== undefined
                        ? formatPercentage(position.actualReturn)
                        : 'N/A'}
                    </p>
                  </div>
                  <Percent className="w-8 h-8 text-green-400" />
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">
                      Days Held
                    </p>
                    <p className="text-2xl font-bold text-purple-900">
                      {Math.round(
                        (new Date().getTime() -
                          new Date(position.createdAt).getTime()) /
                          (1000 * 60 * 60 * 24),
                      )}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-purple-400" />
                </div>
              </div>
            </div>

            {/* Activity Log Feed */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Recent Activity ({recentActivityLogs.length} items)
                </h3>
              </div>

              {recentActivityLogs.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {recentActivityLogs.map((logEntry, index) => (
                    <div
                      key={index}
                      className="p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">{logEntry}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Activity Log Entry
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No activity logs available for this position.</p>
                </div>
              )}
            </div>

            {/* Position Metadata */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Position Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Created:</p>
                  <p className="font-medium">
                    {formatDate(position.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Last Updated:</p>
                  <p className="font-medium">
                    {formatDate(position.updatedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Position ID:</p>
                  <p className="font-medium font-mono text-xs">
                    {position._id}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Account ID:</p>
                  <p className="font-medium font-mono text-xs">
                    {position.accountId}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionDetailsModal;
